import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Divider,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Launch as LaunchIcon,
  Search as SearchIcon,
  Bolt as BoltIcon,
} from '@mui/icons-material';
import type { Patient } from '../../services/patientService';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import {
  useRecentPatientSearchesStore,
  RecentPatientSearchEntry,
} from '../../stores/recentPatientSearchesStore';
import { PatientSearch } from './PatientSearch';
import { PatientQuickViewPanel } from './PatientQuickViewPanel';

interface GlobalPatientSearchDialogProps {
  open: boolean;
  onClose: () => void;
}

function formatDDMMYYYY(dateStr?: string): string {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  if (!y || !m || !d) return dateStr;
  return `${d}/${m}/${y}`;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * GP-308 — Global Patient Search dialog.
 *
 * Layout: left = embedded `PatientSearch` (search input + results list);
 * right = `PatientQuickViewPanel` showing the selected patient's quick view
 * (address, phone, email, active orders, current subscriptions) WITHOUT
 * navigating. The "Open Patient360" affordance is the explicit click-2.
 *
 * When the search input is empty, the dialog shows the user's recent-search
 * history at the top of the results column.
 */
export function GlobalPatientSearchDialog({ open, onClose }: GlobalPatientSearchDialogProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const activePharmacy = usePharmacyStore((s) => s.activePharmacy);
  const recentEntries = useRecentPatientSearchesStore((s) => s.entries);
  const recordRecent = useRecentPatientSearchesStore((s) => s.record);
  const clearRecent = useRecentPatientSearchesStore((s) => s.clear);

  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  // True when the user has typed something into the search field — used to
  // hide the recent-searches header once they're actively searching.
  const [hasQuery, setHasQuery] = useState(false);

  // Reset internal state every time the dialog opens so re-opening doesn't
  // resurrect a stale quick view from a previous session.
  useEffect(() => {
    if (open) {
      setSelectedPatient(null);
      setHasQuery(false);
    }
  }, [open]);

  const handlePreview = useCallback(
    (patient: Patient, pharmacyId: string) => {
      setSelectedPatient(patient);
      recordRecent({
        patientId: patient.id,
        pharmacyId,
        firstName: patient.firstName,
        lastName: patient.lastName,
        dateOfBirth: patient.dateOfBirth,
      });
    },
    [recordRecent],
  );

  const handleOpenPatient360 = useCallback(
    (patient: Patient, pharmacyId: string) => {
      recordRecent({
        patientId: patient.id,
        pharmacyId,
        firstName: patient.firstName,
        lastName: patient.lastName,
        dateOfBirth: patient.dateOfBirth,
      });
      navigate(`/patients/${patient.id}`, { state: { pharmacyId, sourceSystem: 'mqtt' } });
      onClose();
    },
    [navigate, onClose, recordRecent],
  );

  const handleRecentClick = useCallback(
    (entry: RecentPatientSearchEntry) => {
      navigate(`/patients/${entry.patientId}`, {
        state: { pharmacyId: entry.pharmacyId, sourceSystem: 'mqtt' },
      });
      onClose();
    },
    [navigate, onClose],
  );

  const showEmptyHint = !hasQuery && recentEntries.length === 0;
  const showRecent = !hasQuery && recentEntries.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '8%',
          m: 0,
          borderRadius: 3,
          height: '80vh',
          maxHeight: '80vh',
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.25)',
          borderTop: `3px solid ${theme.palette.primary.main}`,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: alpha('#020617', 0.55), backdropFilter: 'blur(6px)' } },
      }}
    >
      {/* Header strip — title + pharmacy chip + close */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Patient Search
          </Typography>
          {activePharmacy && (
            <Chip
              label={activePharmacy.name}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label="Ctrl+P"
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
          />
          <Tooltip title="Close (ESC)">
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden', flex: 1, minHeight: 0 }}>
        {/* Results column */}
        <Box
          sx={{
            flex: selectedPatient ? '0 0 55%' : '1 1 100%',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            transition: 'flex-basis 0.2s ease',
            borderRight: selectedPatient ? `1px solid ${theme.palette.divider}` : 'none',
          }}
        >
          {/* Search input + results.
              GP-956 made PatientSearch's *internal* results list scrollable
              (flex:1 + min-height:0 + overflow), but that only works if this
              wrapper gives it a bounded height. When the user is searching,
              this becomes the height-bounded flex column that hosts the results
              scroll region; otherwise it sizes to just the input so the
              recent/empty body below can take the remaining space. */}
          <Box
            sx={{
              px: 2.5,
              pt: 2,
              pb: hasQuery ? 0 : 1.5,
              borderBottom: hasQuery ? 'none' : `1px solid ${theme.palette.divider}`,
              ...(hasQuery
                ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
                : {}),
            }}
          >
            <PatientSearch
              embedded
              onQueryChange={setHasQuery}
              onPreviewPatient={handlePreview}
              onOpenPatient360={handleOpenPatient360}
              selectedPatientId={selectedPatient?.id ?? null}
            />
          </Box>

          {/* Body — recent searches or empty hint, sits below the input.
              Only rendered when NOT searching: while searching, the wrapper
              above grows to flex:1 to host the scrollable results, so this
              must not also claim flex space (which would split the column). */}
          {!hasQuery && (
          <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 2 }}>
            {showRecent && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        color: 'text.secondary',
                      }}
                    >
                      Recently viewed · {recentEntries.length}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    onClick={clearRecent}
                    sx={{
                      cursor: 'pointer',
                      color: 'text.secondary',
                      '&:hover': { color: 'error.main', textDecoration: 'underline' },
                    }}
                  >
                    Clear
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {recentEntries.map((entry) => (
                    <Box
                      key={entry.patientId}
                      onClick={() => handleRecentClick(entry)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 1.5,
                        py: 1,
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 220 }} noWrap>
                        {entry.lastName}, {entry.firstName}
                      </Typography>
                      {entry.dateOfBirth && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ minWidth: 130, fontVariantNumeric: 'tabular-nums' }}
                        >
                          DOB {formatDDMMYYYY(entry.dateOfBirth)}
                        </Typography>
                      )}
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        {timeAgo(entry.searchedAt)}
                      </Typography>
                      <LaunchIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {showEmptyHint && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: 240,
                  textAlign: 'center',
                  color: 'text.secondary',
                  px: 4,
                }}
              >
                <SearchIcon sx={{ fontSize: 56, color: alpha(theme.palette.primary.main, 0.35), mb: 1.5 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                  Find a patient
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Type a last name above — or <strong>Lastname, Firstname</strong> for a tighter match.
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 3,
                    mt: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <BoltIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption">
                      Click a row to preview without leaving the page
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <LaunchIcon sx={{ fontSize: 16 }} />
                    <Typography variant="caption">
                      Use <strong>View 360°</strong> to open the full profile
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          )}
        </Box>

        {/* Quick view panel — only mounted when a patient is selected */}
        {selectedPatient && (
          <Box sx={{ flex: '1 1 45%', minWidth: 0, overflow: 'auto', px: 2.5, py: 2 }}>
            <PatientQuickViewPanel
              patient={selectedPatient}
              pharmacyId={activePharmacy?.id ?? null}
              onOpenPatient360={() => {
                if (!activePharmacy) return;
                handleOpenPatient360(selectedPatient, activePharmacy.id);
              }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
